import React, { useState, useCallback } from "react";
import { Button, Icon, Checkbox } from "semantic-ui-react";
import { map, filter, size, uniqBy } from "lodash";
import { TableAdmin } from "../";
import { TABLE_KINDS } from "../../../../utils/constants";
import { usePolling } from "../../../../hooks";
import "./TablesListAdmin.scss";

// mode "salon" (Pedidos, por defecto): solo las mesas del salón.
// mode "external" (Venta rápida y delivery): las mesas virtuales, agrupadas
// por tipo — separadas en su propia pantalla para que no queden al fondo
// cuando el salón tiene muchas mesas (pedido del usuario).
// Pestaña elegida en Pedidos: se recuerda por dispositivo (una tablet fija en
// el Piso 2 queda siempre ahí).
const AREA_TAB_KEY = "icard.areaTab";
const ALL = "all";
const readTab = () => {
  try {
    return localStorage.getItem(AREA_TAB_KEY) || ALL;
  } catch (error) {
    return ALL;
  }
};

export function TablesListAdmin(props) {
  const { tables, mode = "salon" } = props;
  const [reload, setReload] = useState(false);
  const [autoReload, setAutoReload] = useState(false);
  const [tab, setTab] = useState(readTab);
  // {tableId: {occupied, ready}}: lo informa cada mesa (TableAdmin.onStatus).
  const [status, setStatus] = useState({});
  const onStatus = useCallback(
    (id, value) =>
      setStatus((prev) =>
        prev[id]?.occupied === value.occupied && prev[id]?.ready === value.ready
          ? prev
          : { ...prev, [id]: value }
      ),
    []
  );

  const onReload = () => setReload((prev) => !prev);
  const isVirtual = (table) => table.kind && table.kind !== "SALON";
  const salonTables = filter(tables, (table) => !isVirtual(table));
  // Ambientes con mesas, en el orden que les dio el Admin (la API ya ordena las
  // mesas por ambiente y número).
  const areas = uniqBy(
    salonTables
      .filter((table) => table.area)
      .map((table) => ({ id: String(table.area), name: table.area_name })),
    "id"
  );
  const multiArea = size(areas) > 1;
  const currentTab = multiArea && areas.some((a) => a.id === tab) ? tab : ALL;
  const chooseTab = (value) => {
    setTab(value);
    try {
      localStorage.setItem(AREA_TAB_KEY, value);
    } catch (error) {
      // sin almacenamiento: solo dura mientras la pantalla esté abierta
    }
  };
  const tablesOf = (areaId) =>
    areaId === ALL ? salonTables : salonTables.filter((t) => String(t.area) === areaId);
  const counters = (areaId) => {
    const list = tablesOf(areaId);
    return {
      occupied: list.filter((t) => status[t.id]?.occupied).length,
      ready: list.filter((t) => status[t.id]?.ready).length,
    };
  };
  const virtualGroups = TABLE_KINDS.filter((kind) => kind.value !== "SALON")
    .map((kind) => ({ ...kind, tables: filter(tables, (table) => table.kind === kind.value) }))
    .filter((group) => size(group.tables) > 0);

  // Refresco automático cada 5 s con usePolling: antes era una cadena de
  // setTimeout que nunca se cancelaba (por eso apagarlo recargaba la página
  // entera) y que en el celular del mozo (iPhone/iPad) se congelaba.
  usePolling(onReload, 5000, { enabled: autoReload, immediate: true });

  const onCheckAutoReload = (check) => setAutoReload(check);

  return (
    <div className="tables-list-admin">
      <div className="tables-list-admin__toolbar">
        <div className="tables-list-admin__reaload-toggle">
          <span>Refresco automático</span>
          <Checkbox
            toggle
            checked={autoReload}
            onChange={(_, data) => onCheckAutoReload(data.checked)}
          />
        </div>

        <Button
          primary
          icon
          title="Refresco manual"
          className="tables-list-admin__reaload"
          onClick={onReload}
        >
          <Icon name="refresh" />
        </Button>
      </div>

      {mode === "salon" && multiArea && (
        // Pestañas por ambiente: "Todos" y una por ambiente, con cuántas mesas
        // están ocupadas y un aviso morado si hay platos listos para llevar.
        <div className="tables-list-admin__tabs" role="tablist">
          {[{ id: ALL, name: "Todos" }, ...areas].map((area) => {
            const count = counters(area.id);
            return (
              <button
                key={area.id}
                type="button"
                role="tab"
                aria-selected={currentTab === area.id}
                className={`tables-list-admin__tab${currentTab === area.id ? " tables-list-admin__tab--active" : ""}`}
                onClick={() => chooseTab(area.id)}
              >
                {area.name}
                {count.occupied > 0 && (
                  <span className="tables-list-admin__tab-count" title="Mesas ocupadas">
                    {count.occupied}
                  </span>
                )}
                {count.ready > 0 && (
                  <span className="tables-list-admin__tab-ready" title="Mesas con platos listos">
                    {count.ready} listo{count.ready > 1 ? "s" : ""}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {mode === "salon" &&
        // Todas las mesas se montan siempre (así los contadores de las otras
        // pestañas siguen al día); se ocultan las que no son de la pestaña.
        // En "Todos", con varios ambientes, cada uno lleva su título: los números
        // se repiten entre ambientes.
        (multiArea && currentTab === ALL
          ? areas.map((area) => (
              <div key={area.id} className="tables-list-admin__area">
                <h3>{area.name}</h3>
                <div className="tables-list-admin__group-tables">
                  {map(tablesOf(area.id), (table) => (
                    <TableAdmin key={table.id} table={table} reload={reload} onStatus={onStatus} />
                  ))}
                </div>
              </div>
            ))
          : map(salonTables, (table) => (
              <div
                key={table.id}
                className="tables-list-admin__cell"
                hidden={multiArea && String(table.area) !== currentTab}
              >
                <TableAdmin table={table} reload={reload} onStatus={onStatus} />
              </div>
            )))}

      {mode === "external" &&
        (size(virtualGroups) === 0 ? (
          <p className="tables-list-admin__empty">
            Todavía no hay mesas de venta rápida, WhatsApp o delivery. Créalas en
            Configuración → Mesas (tipo, número interno y nombre, ej. "Delivery 1").
          </p>
        ) : (
          // Grupos lado a lado (Venta rápida | WhatsApp | Delivery), cada uno
          // con su título: con pocas mesas por tipo no se desperdicia una
          // fila entera por grupo.
          map(virtualGroups, (group) => (
            <div key={group.value} className="tables-list-admin__group">
              <h3>{group.text}</h3>
              <div className="tables-list-admin__group-tables">
                {map(group.tables, (table) => (
                  <TableAdmin key={table.number} table={table} reload={reload} />
                ))}
              </div>
            </div>
          ))
        ))}
    </div>
  );
}
